import {
  FigureSelectionArea,
  RegionOfInterest,
  RegionSelectionJson,
  RolodexSyncType,
  Ruler,
  useRolodexSync,
} from 'features/fde'
import { useRolodexEntries } from 'features/rolodex'
import { useCallback, useMemo } from 'react'
import {
  GeckoComponent,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  RolodexEntryJson,
  RolodexEntryTagsType,
  RolodexEntryType,
} from 'types'

type RolodexRuler = Ruler & {
  tags: RolodexEntryTagsType
}

const DEFAULT_UNIT = GeckoMeasurementUnit.Inches

const RD_TYPE_RULER = RolodexEntryType.RULER

function selectionToJson(selection: FigureSelectionArea): RegionSelectionJson {
  return {
    x: {
      start: {
        num: selection.x.start.num,
        unit: selection.x.start.unit.id,
      },
      end: {
        num: selection.x.end.num,
        unit: selection.x.end.unit.id,
      },
    },
    y: {
      start: {
        num: selection.y.start.num,
        unit: selection.y.start.unit.id,
      },
      end: {
        num: selection.y.end.num,
        unit: selection.y.end.unit.id,
      },
    },
  }
}

function selectionFromJson(json: RegionSelectionJson): FigureSelectionArea {
  return {
    x: GeckoMeasurementRange.CreateFromStartEnd(
      GeckoMeasurement.CreateFromValue(
        json.x.start.num,
        GeckoMeasurementUnit.GetByID(json.x.start.unit) ?? DEFAULT_UNIT
      ),
      GeckoMeasurement.CreateFromValue(
        json.x.end.num,
        GeckoMeasurementUnit.GetByID(json.x.end.unit) ?? DEFAULT_UNIT
      )
    ),
    y: GeckoMeasurementRange.CreateFromStartEnd(
      GeckoMeasurement.CreateFromValue(
        json.y.start.num,
        GeckoMeasurementUnit.GetByID(json.y.start.unit) ?? DEFAULT_UNIT
      ),
      GeckoMeasurement.CreateFromValue(
        json.y.end.num,
        GeckoMeasurementUnit.GetByID(json.y.end.unit) ?? DEFAULT_UNIT
      )
    ),
  }
}

function rulerToEntry(region: RolodexRuler): RolodexEntryJson {
  return {
    id: region.id,
    type: RD_TYPE_RULER,
    data: {
      title: region.title,
      selection: selectionToJson(region.selection),
      wraps: region.wraps,
    },
    tags: region.tags,
    index: {},
    updated_at: new Date(Date.now()).toISOString(),
    generation: 1,
    view_restriction: null,
  }
}

function entryToRuler(entry: RolodexEntryJson): RolodexRuler {
  return {
    id: entry.id,
    title: entry.data.title as string,
    selection: selectionFromJson(entry.data.selection as RegionSelectionJson),
    wraps: entry.data.wraps as RolodexRuler['wraps'] | undefined,
    tags: entry.tags,
  }
}

export function useRolodexRulers(tags: RolodexEntryTagsType | undefined): {
  rulers: ReadonlyArray<Ruler>
  isLoading: boolean
} {
  const { entries, isLoading } = useRolodexEntries(
    useMemo(
      () =>
        tags
          ? [
              {
                type: RD_TYPE_RULER,
                tags,
              },
            ]
          : [],
      [tags]
    ),
    { allowCache: false }
  )
  return useMemo(
    () => ({
      rulers: entries.map(entryToRuler),
      isLoading,
    }),
    [entries, isLoading]
  )
}

export function useRolodexSyncRulers(
  selectedComponent: GeckoComponent | undefined,
  customTags?: RolodexEntryTagsType
): RolodexSyncType<Ruler> {
  const componentID = selectedComponent?.id

  const rulerQuery = useMemo(() => {
    return {
      type: RD_TYPE_RULER,
      tags: {
        component: componentID ?? '',
        ...customTags,
      },
    }
  }, [componentID, customTags])
  const toEntry = useCallback(rulerToEntry, [])
  const fromEntry = useCallback(entryToRuler, [])

  const rdSync = useRolodexSync<RolodexRuler>(rulerQuery, toEntry, fromEntry)

  const { tags: componentTags } = selectedComponent ?? { tags: {} }
  const setValues = useCallback(
    (vals: ReadonlyArray<RegionOfInterest>) => {
      rdSync.setValues(
        vals.map((val) => {
          return {
            ...val,
            tags: {
              ...componentTags,
              component: componentID ?? '',
            },
          }
        })
      )
    },
    [componentID, componentTags, rdSync]
  )

  return {
    values: rdSync.values,
    setValues,
    isLoading: rdSync.isLoading,
  }
}
